import type {
    BioscopeDataQuality,
    FeedingPen,
    FeedingSeriesPoint,
} from '@/domain/feedingDashboard';

/**
 * The quality state for ONE surface, from the site-wide state plus whether that
 * surface actually has a value to show.
 *
 * Without this every dot on the page carries the same site-wide state, which makes
 * eight of the nine redundant and, worse, wrong on any panel whose own value is
 * missing for its own reason. A site can have plenty of Bioscope weight (site state
 * `ready`) while RGI Bioscope is blank because `weight_pms_plan` is unmapped. A green
 * dot on that panel repeats the mistake the `noData` state was added to fix, one level
 * down.
 *
 * The site state is the best selected pen, and the Bioscope values come from ready pens
 * only. `collecting` therefore means no pen is ready: every Bioscope figure is withheld,
 * and reporting `noData` there would blame the integration for a state the dashboard
 * itself imposed. A site-wide `noData` likewise leaves no Bioscope figure to show.
 *
 * The narrowing only ever runs one way, which for the Bioscope surfaces costs nothing.
 * Pellet surfaces do not go through this: their values follow pellet data, not weight
 * readiness (O10), so they rate their own data through `pelletSurfaceQuality`.
 */
export function surfaceQuality(
    siteQuality: BioscopeDataQuality,
    hasValue: boolean
): BioscopeDataQuality {
    if (siteQuality === 'collecting') return 'collecting';
    if (siteQuality === 'noData') return 'noData';
    return hasValue ? 'ready' : 'noData';
}

/** A pellet surface's state. Pellet data has no startup window, so never `collecting`. */
export type PelletDataQuality = Exclude<BioscopeDataQuality, 'collecting'>;

/**
 * The quality state for a pellet fallthrough surface. Pellet counts come from the
 * feeding camera, not from the weight estimates, so the site's weight state (startup
 * window, estimate outage) says nothing about them; IPS 3.1.2 lists the two
 * availabilities separately. Pellet data has no startup window, so the only states are
 * "has pellet data" and "has none". The dot words them for pellets (`qualitySubject`),
 * since a pen can have pellets while its estimates are not yet reliable.
 */
export function pelletSurfaceQuality(available: boolean): PelletDataQuality {
    return available ? 'ready' : 'noData';
}

/**
 * The Feed & Weight panel's state: its WORST contributing pen (F4c), read from the series
 * the chart draws.
 *
 * The chart draws ready pens as a line and collecting pens as points, so a mixed
 * selection shows startup estimates and the dot has to say so. The pen states cannot
 * tell: they stop at the closing day while the chart runs to today, so a pen whose only
 * estimate is today's reads noData there and is still drawn. The series already carries
 * the chart's own split, so the dot follows what is on screen. A pen with no estimate
 * adds no point and does not pull the dot down: the line it is absent from is still
 * reliable.
 */
export function feedWeightQuality(
    series: readonly Pick<FeedingSeriesPoint, 'weightBioscopeG' | 'weightBioscopeCollectingG'>[]
): BioscopeDataQuality {
    if (series.some((point) => point.weightBioscopeCollectingG != null)) return 'collecting';
    if (series.some((point) => point.weightBioscopeG != null)) return 'ready';
    return 'noData';
}

/**
 * Whether a per-pen Bioscope surface has a value to draw. Only a ready pen's Bioscope value
 * is drawn, so a collecting pen's must not turn the dot green beside an empty chart.
 */
export function readyPenHas(
    pens: FeedingPen[],
    value: (pen: FeedingPen) => number | null
): boolean {
    return pens.some((pen) => pen.bioscopeDataQuality === 'ready' && value(pen) != null);
}
